"use client";

import React, { memo } from "react";
// import Image from "next/image";
import SmartImage from "~/components/reusables/SmartImage";
import { LinkTrackers } from "~/components/reusables/link-trackers";

// --- Types ---

interface CaseStudyHeroProps {
  industry: string;
  title?: string;
  data: {
    title?: string;
    industry?: string;
    companySize?: string;
    solution?: string;
    solutionLink?: string;
    logo?: { url: string; alternativeText?: string };
    description?: string;
    backgroundImage?: { url: string };
    background?: {
      id: number;
      gradientType: string;
      colorFrom: string;
      colorTo: string;
    };
  };
}

// --- Sub-components ---

const SectionLabel = memo(({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <p className={`leading-none not-italic relative shrink-0 text-base tracking-[0.48px] uppercase ${className}`}>
    {children}
  </p>
));
SectionLabel.displayName = "SectionLabel";

const BadgeTLDR = memo(() => (
  <div className="flex items-center justify-center p-2 relative rounded-[4px] shrink-0 w-fit">
    <div aria-hidden="true" className="absolute border border-solid border-white inset-0 pointer-events-none rounded-[4px]" />
    <SectionLabel className="text-white whitespace-nowrap px-2 font-medium">TL;DR</SectionLabel>
  </div>
));
BadgeTLDR.displayName = "BadgeTLDR";

// --- Main Component ---

const CaseStudyHero = memo(({ data, industry, title }: CaseStudyHeroProps) => {
  
  // Simplified logic: Using direct S3 URLs
  const getBackgroundStyle = () => {
    if (data?.backgroundImage?.url) {
      return { 
        backgroundImage: `url(${data.backgroundImage.url})`, 
        backgroundSize: 'cover', 
        backgroundPosition: 'center' 
      };
    }

    if (data?.background) {
      const { gradientType, colorFrom, colorTo } = data.background;
      const isRadial = gradientType?.toLowerCase() === "radial";
      const gradientFunction = isRadial ? "radial-gradient" : "linear-gradient";
      const direction = isRadial ? "circle" : "to bottom right";

      return {
        background: `${gradientFunction}(${direction}, ${colorFrom}, ${colorTo})`,
        backgroundSize: 'cover'
      };
    }

    return { 
      backgroundImage: "url('data:image/svg+xml;utf8,<svg viewBox=\'0 0 2050 805\' xmlns=\'http://www.w3.org/2000/svg\' preserveAspectRatio=\'none\'><rect x=\'0\' y=\'0\' height=\'100%\' width=\'100%\' fill=\'url(%23grad)\' opacity=\'1\'/><defs><radialGradient id=\'grad\' gradientUnits=\'userSpaceOnUse\' cx=\'0\' cy=\'0\' r=\'10\' gradientTransform=\'matrix(163.51 100.67 -43.812 375.71 29.028 -427.1)\'><stop stop-color=\'rgba(0,81,173,1)\' offset=\'0\'/><stop stop-color=\'rgba(0,135,204,1)\' offset=\'0.5\'/><stop stop-color=\'rgba(0,188,235,1)\' offset=\'1\'/></radialGradient></defs></svg>')",
      backgroundSize: 'cover'
    };
  };

  return (
    <section 
      className="flex flex-col items-start justify-end relative w-full min-h-[600px] bg-black" 
      style={data?.background?.gradientType ? getBackgroundStyle() : undefined}
    >
      <div className="flex flex-col items-start w-full">
        <div className="container mx-auto relative w-full">
          <div className="flex flex-col gap-6 items-start py-12 lg:py-16">
            {data?.logo?.url && (
              <header className="relative w-48 md:w-[360px] h-16 md:h-[119px]">
                {/* <Image 
                  alt={data.logo.alternativeText || "Company Logo"} 
                  className="object-contain object-left" 
                  src={data.logo.url} // Direct S3 URL
                  fill 
                  priority
                /> */}
                <SmartImage
                  src={data.logo.url}
                  alt={"Company Logo"}
                  className="object-contain object-left"
                  fill
                />
              </header>
            )}
            
            <h1 className="leading-tight font-light text-4xl md:text-6xl lg:text-[80px] text-white">
              {title}
            </h1>

            <article className="flex flex-col gap-4 items-start py-6 w-full font-light">
              <BadgeTLDR />
              <div 
                className="text-white
                  [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:ml-6
                  [&_li]:text-base md:[&_li]:text-xl 
                  [&_span]:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: data?.description || "" }} 
              />
            </article>
          </div>
        </div>
        
        {/* Info Bar */}
        <div className="w-full bg-black/40 backdrop-blur-sm border-t border-white/10">
          <div className="container mx-auto py-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white">
              <div className="space-y-2">
                <SectionLabel className="font-medium text-base opacity-70">Industry</SectionLabel>
                <p className="text-sm md:text-xl font-light">{industry}</p>
              </div>
              <div className="space-y-2">
                <SectionLabel className="font-medium text-base opacity-70">Company Size</SectionLabel>
                <p className="text-sm md:text-xl font-light">{data?.companySize}</p>
              </div>
              <div className="space-y-2">
                <SectionLabel className="font-medium text-base opacity-70">Solution</SectionLabel>
                <div className="text-sm md:text-xl font-light hover:underline hover:text-blue-500">
                  <LinkTrackers 
                    href={data?.solutionLink || "/"} 
                    target="_self" 
                    segmentMsg="Solution link clicked" 
                    segmentOpt={{ solution_name: data?.solution }}
                    className="hover:text-blue-400 transition-colors underline-offset-4"
                  >
                    {data?.solution}
                  </LinkTrackers>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

CaseStudyHero.displayName = "CaseStudyHero";

export default CaseStudyHero;